import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Phone, Menu, X, TrainFront, ArrowUpRight } from "lucide-react";
import { KOCHI } from "../mock";

const KOCHI_NAV = [
  { label: "Home", href: "#top" },
  { label: "Rooms", href: "#rooms" },
  { label: "Facilities", href: "#facilities" },
  { label: "Location", href: "#location" },
  { label: "Contact", href: "#contact" },
];

export const KochiHeader = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header className="fixed top-0 left-0 w-full z-50">
      {/* Umbrella ribbon */}
      <div className="bg-[#0f2a44] text-white text-[11px] md:text-xs">
        <div className="max-w-7xl mx-auto px-5 md:px-8 h-8 flex items-center justify-between">
          <span className="tracking-wide">A property of Auracrest Haven Resorts</span>
          <Link to="/" className="flex items-center gap-1 text-[#ffb3b3] hover:text-white transition-colors">
            Visit Auracrest Haven, Munnar <ArrowUpRight className="w-3.5 h-3.5" />
          </Link>
        </div>
      </div>

      <div
        className={`transition-colors duration-300 ${
          scrolled ? "bg-white/95 backdrop-blur shadow-sm" : "bg-white"
        }`}
      >
        <div className="max-w-7xl mx-auto px-5 md:px-8 h-[70px] flex items-center justify-between">
          <a href="#top" className="flex items-center">
            <img
              src={KOCHI.logo}
              alt="Morning Star Metro Home Stay Kochi"
              className="h-11 md:h-12 w-auto object-contain"
            />
          </a>

          <nav className="hidden lg:flex items-center gap-8">
            {KOCHI_NAV.map((l) => (
              <a
                key={l.label}
                href={l.href}
                className="text-[13px] font-medium tracking-wide text-neutral-700 hover:text-[#e11414] transition-colors"
              >
                {l.label}
              </a>
            ))}
          </nav>

          <div className="flex items-center gap-4">
            <a
              href={`tel:${KOCHI.phone}`}
              className="hidden sm:flex items-center gap-2 font-bold text-[#0f2a44]"
            >
              <Phone className="w-4 h-4" />
              {KOCHI.phone}
            </a>
            <button
              className="lg:hidden text-neutral-800"
              onClick={() => setOpen((v) => !v)}
              aria-label="menu"
            >
              {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {open && (
          <div className="lg:hidden bg-white border-t border-neutral-100 px-5 py-4 flex flex-col gap-4">
            {KOCHI_NAV.map((l) => (
              <a
                key={l.label}
                href={l.href}
                onClick={() => setOpen(false)}
                className="text-sm font-medium text-neutral-700 hover:text-[#e11414]"
              >
                {l.label}
              </a>
            ))}
            <a href={`tel:${KOCHI.phone}`} className="flex items-center gap-2 font-bold text-[#0f2a44]">
              <Phone className="w-4 h-4" /> {KOCHI.phone}
            </a>
            <Link to="/" className="flex items-center gap-1 text-[#e11414] font-medium">
              Visit Auracrest Haven, Munnar <ArrowUpRight className="w-4 h-4" />
            </Link>
          </div>
        )}
      </div>
    </header>
  );
};

export const KochiFooter = () => (
  <footer className="bg-[#0f2a44] text-neutral-300 py-14">
    <div className="max-w-7xl mx-auto px-5 md:px-8 grid md:grid-cols-4 gap-10">
      <div className="md:col-span-1">
        <div className="bg-white rounded-xl inline-block px-4 py-3 mb-4">
          <img
            src={KOCHI.logo}
            alt="Morning Star Metro Home Stay Kochi"
            className="h-12 w-auto object-contain"
          />
        </div>
        <p className="text-sm leading-7">{KOCHI.tagline}</p>
      </div>

      <div>
        <h4 className="text-white font-semibold mb-4">Contact</h4>
        <p className="text-sm leading-7">{KOCHI.location}</p>
        <p className="text-sm mt-2">{KOCHI.phone}</p>
        <p className="text-sm">{KOCHI.email}</p>
      </div>

      <div>
        <h4 className="text-white font-semibold mb-4">Explore</h4>
        <ul className="space-y-2 text-sm">
          <li><a href="#rooms" className="hover:text-[#ffb3b3] transition-colors">Rooms &amp; Comforts</a></li>
          <li><a href="#facilities" className="hover:text-[#ffb3b3] transition-colors">Facilities</a></li>
          <li><a href="#location" className="hover:text-[#ffb3b3] transition-colors">Location</a></li>
          <li><a href="#contact" className="hover:text-[#ffb3b3] transition-colors">Contact</a></li>
          <li><a href="/privacy_policy.html" className="hover:text-[#ffb3b3] transition-colors">Privacy Policy</a></li>
        </ul>
      </div>

      <div>
        <h4 className="text-white font-semibold mb-4">Auracrest Haven Resorts</h4>
        <ul className="space-y-2 text-sm">
          <li className="flex items-center gap-1">
            <TrainFront className="w-4 h-4 text-[#ffb3b3]" /> Morning Star Metro Home Stay Kochi
          </li>
          <li>
            <Link to="/" className="hover:text-[#ffb3b3] transition-colors">
              Auracrest Haven, Munnar &rarr;
            </Link>
          </li>
          <li className="text-neutral-400 italic">More properties coming soon</li>
        </ul>
      </div>
    </div>
    <div className="max-w-7xl mx-auto px-5 md:px-8 mt-10 pt-6 border-t border-white/10 text-sm text-center">
      &copy; {new Date().getFullYear()} Auracrest Haven Resorts. All rights reserved.
    </div>
  </footer>
);
